<script setup>
import {
  computed, onMounted, ref, toValue, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { equals, path } from 'ramda';
import { useI18n } from 'vue-i18n';
import ChoosePlansComponent from '@/components/register/ChoosePlanComponent.vue';
import BillingComponent from '@/components/register/BillingComponent.vue';
import ProcessCircles from '@/components/register/ProcessCircles.vue';
import ConfirmationComponent from '@/components/register/ConfirmationComponent.vue';
import RegisterNavigationBar from '@/components/navigation/RegisterNavigationBar.vue';
import logInDevMode from '@/services/backend/logInDevMode';
import CreateAccountStepComponent from '@/components/register/CreateAccountStepComponent.vue';
import { isIndividualPlan, isNotNullAndNotEmpty } from '@/utilities';
import { trackEvent } from '@/services/analytics/googleAnalytics.js';
import { isLocalDevMode } from '../../config';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const isPaymentReturn = computed(() => isNotNullAndNotEmpty(route.params.success));
const wasPaymentSuccess = computed(() => equals(route.params.success, 'true'));
const freePaymentSuccess = ref(false);
const wasFreePaymentSuccess = computed(() => freePaymentSuccess.value);
const RegisterSteps = {
  ChoosePlan: 0,
  AccountDetails: 1,
  Billing: 2,
  Confirmation: 3,
};

const currentRegisterStep = ref(RegisterSteps.ChoosePlan);

onMounted(() => {
  if (toValue(isPaymentReturn)) {
    currentRegisterStep.value = RegisterSteps.Confirmation;
  } else {
    trackRegisterStepEvent(currentRegisterStep.value);
  }
});

const stepNames = {
  0: 'ChoosePlan',
  1: 'AccountDetails',
  2: 'Billing',
  3: 'Confirmation',
};

function trackRegisterStepEvent(newStep, oldStep) {
  const stepName = stepNames[newStep] || `Step${newStep}`;
  const label = oldStep !== undefined
    ? `Changed from ${stepNames[oldStep]} to ${stepName}`
    : `Initial step ${stepName}`;

  trackEvent(`register_step_${stepName}`, {
    event_category: 'Register',
    event_label: label,
    step: newStep,
  });
}

watch(currentRegisterStep, (newStep, oldStep) => {
  trackRegisterStepEvent(newStep, oldStep);
});
const emailForPayment = ref('');
const chosenCountry = ref('');
const chosenVat = ref(0);

const chosenPlan = ref();

function onPlanChosen(plan) {
  chosenPlan.value = plan;
  currentRegisterStep.value = RegisterSteps.AccountDetails;

  trackEvent('plan_chosen', {
    event_category: 'Register',
    event_label: plan.name,
  });
}

function calculateVatByCountry(country) {
  if (country === 'de') {
    return 0.19;
  }
  return 0;
}

function onDetailsConfirmed(values) {
  emailForPayment.value = path(['user', 'email'], values);
  chosenCountry.value = isIndividualPlan(chosenPlan.value) ? path(['user', 'country'], values) : path(['company', 'country'], values);
  const country = chosenCountry.value;
  chosenVat.value = calculateVatByCountry(country);
  logInDevMode('Country ', country, 'VAT', chosenVat.value);
  logInDevMode(`Using email ${emailForPayment.value} for Billing step.)`);
  currentRegisterStep.value = RegisterSteps.Billing;
}

function onPaymentSuccess() {
  currentRegisterStep.value = RegisterSteps.Confirmation;
  trackEvent('payment_success', {
    event_category: 'Payments',
    event_label: wasPaymentSuccess.value,
  });
}
function onFreePayment() {
  freePaymentSuccess.value = true;
  currentRegisterStep.value = RegisterSteps.Confirmation;

  trackEvent('free_payment_success', {
    event_category: 'Payments',
    event_label: freePaymentSuccess.value,
  });
}

function onGoBack() {
  switch (currentRegisterStep.value) {
    case RegisterSteps.ChoosePlan:
      router.push('/');
      break;
    case RegisterSteps.AccountDetails:
      currentRegisterStep.value = RegisterSteps.ChoosePlan;
      break;
    case RegisterSteps.Billing:
      currentRegisterStep.value = RegisterSteps.AccountDetails;
      break;
    case RegisterSteps.Confirmation:
      // already finished, noop
      break;
    default:
      router.push('/');
      break;
  }
}

const stepsText = {
  plans: t('registerView.processCircle.plan'),
  accountDetails: t('registerView.processCircle.accountDetails'),
  billingInformation: t('registerView.processCircle.billing'),
  confirmation: t('registerView.processCircle.confirmation'),
};

const steps = [
  {
    name: stepsText.plans,
    isDone: () => currentRegisterStep.value > RegisterSteps.ChoosePlan,
    isCurrent: () => currentRegisterStep.value === RegisterSteps.ChoosePlan,
  },
  {
    name: stepsText.accountDetails,
    isDone: () => currentRegisterStep.value > RegisterSteps.AccountDetails,
    isCurrent: () => currentRegisterStep.value === RegisterSteps.AccountDetails,
  },
  {
    name: stepsText.billingInformation,
    isDone: () => currentRegisterStep.value > RegisterSteps.Billing,
    isCurrent: () => currentRegisterStep.value === RegisterSteps.Billing,
  },
  {
    name: stepsText.confirmation,
    // confirmation is basically always done automatically
    isDone: () => currentRegisterStep.value === RegisterSteps.Confirmation,
    isCurrent: () => false,
  },
];

function devSkipStep() {
  currentRegisterStep.value++;
  if (currentRegisterStep.value === RegisterSteps.Billing) {
    emailForPayment.value = 'skippedEmailSo@usethistotest.com';
    logInDevMode(`Skipped account. Will use test email ${emailForPayment.value}`);
  }
}

function stepShortcutClicked(idx) {
  if (currentRegisterStep.value >= RegisterSteps.Billing) {
    return;
  }

  if (idx < 0 || idx >= currentRegisterStep.value) {
    return;
  }

  currentRegisterStep.value = idx;
}
</script>

<template>
  <RegisterNavigationBar />

  <button
    v-if="isLocalDevMode === true"
    @click="devSkipStep"
  >
    DEV SKIP STEP
  </button>

  <div class="w-full h-full ">
    <div class="mx-auto max-w-7xl pt-24">
      <ProcessCircles
        :steps="steps"
        size="24"
        @request-step-shortcut="stepShortcutClicked"
      />

      <div class="pt-24">
        <ChoosePlansComponent
          @plan-chosen="onPlanChosen"
          @back="onGoBack"
          v-if="currentRegisterStep === RegisterSteps.ChoosePlan"
        />

        <template v-if="currentRegisterStep === RegisterSteps.AccountDetails">
          <CreateAccountStepComponent
            :chosen-plan="chosenPlan"
            @details-confirmed="onDetailsConfirmed"
            @back="onGoBack"
          />
        </template>

        <BillingComponent
          :email-for-payment="emailForPayment"
          :chosen-plan="chosenPlan"
          :vat="chosenVat"
          @payment-success="onPaymentSuccess"
          @free-payment-success="onFreePayment"
          @back="onGoBack"
          v-if="currentRegisterStep === RegisterSteps.Billing"
        />

        <ConfirmationComponent
          @back="onGoBack"
          v-if="currentRegisterStep === RegisterSteps.Confirmation"
          :payment-success="wasPaymentSuccess"
          :free-payment-success="wasFreePaymentSuccess"
        />
      </div>
    </div>
  </div>
</template>
